import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { useAudioPlayerStore } from '@/stores/audioPlayerStore';
import { FlexSBCBox } from '@/styles/Boxes';
import { Icon, Icons } from '@/styles/Icon';
import { CardTitle, SubTitle } from '@/styles/Typography';
import { CardTrack, PlayerTrack } from '@/types/track_types';
import { formatCommas, formatDate } from '@/utils/dateUtil';
import { getStreamingUrl } from '@/utils/audioPlayerUtils';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { styled, useTheme } from 'styled-components';
import { BuyButtonComponent, GradientButton } from '../button/Button';
import { PlayIconWrapper, PlayOverlay, hoverPlayOverlay } from '../common/PlayOverlay';
import { ProgressBar } from '../common/ProgressBar';
import { getCoverImageUrl } from '@/utils/mediaUtils';
import { sanitizeText } from '@/utils/xssProtection';
import { OptimizedImage } from '../common/OptimizedImage';

interface MusicCardUnifiedProps {
  data: CardTrack;
  type?: 'main' | 'mypage';
  showPurchasedActions?: boolean;
  isInMyWallet?: boolean;
  onCertificateClick?: () => void;
  onDownloadClick?: () => void;
  onTransferClick?: () => void;
  remainingDownloads?: number; // 남은 다운로드 횟수
  totalDownloads?: number; // 총 다운로드 횟수 (기본값 5회)
  enableScrollAnimation?: boolean; // 스크롤 애니메이션 활성화 여부
  animationDelay?: number; // 애니메이션 딜레이
}

export const MusicCardUnified: React.FC<MusicCardUnifiedProps> = ({
  data,
  type = 'main',
  showPurchasedActions = true,
  onCertificateClick,
  onDownloadClick,
  onTransferClick,
  remainingDownloads,
  totalDownloads = 5, // 기본값 5회
  enableScrollAnimation = false,
  animationDelay = 0,
}) => {
  const navigate = useNavigate();
  const purchaseRate = (data.nft.soldCount / data.nft.totalSupply) * 100;
  const theme = useTheme();

  // 홈 화면에서 스크롤에 따라 애니메이션이 트리거 되기 위한 Intersection Observer 훅 사용
  const { ref, isInView } = useIntersectionObserver({
    threshold: 0.1, // 요소의 10%가 보이면 트리거
    triggerOnce: true, // 한 번만 실행
  });

  const { currentTrack, isPlaying, playTrack, togglePlay } = useAudioPlayerStore();
  const isCurrentTrack = currentTrack?.id === data.id;
  const isCurrentlyPlaying = isCurrentTrack && isPlaying;

  const handleCardClick = () => {
    if (isCurrentTrack) {
      togglePlay();
    } else {
      // CardTrack을 PlayerTrack으로 변환하여 재생
      // 백엔드 스트리밍 API 엔드포인트 사용: /api/tracks/{track_number}/stream/
      const playerTrack: PlayerTrack = {
        id: data.id,
        title: data.title,
        artistId: data.artistId,
        artistName: data.artistName,
        coverImage: getCoverImageUrl(data.coverImage),
        audioUrl: getStreamingUrl(Number(data.id)),
        duration: data.duration,
        isPurchased: data.isPurchased,
        isInCart: data.isInCart,
        status: data.saleInfo.status,
      };
      playTrack(playerTrack);
    }
  };

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/product/${data.id}`);
  };

  // 키보드 접근성: Enter/Space 키 처리
  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick();
    }
  };

  const handleInfoKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleInfoClick(e as unknown as React.MouseEvent);
    }
  };

  // ✅ remainingDownloads 기본값 처리 개선
  const actualRemainingDownloads = remainingDownloads ?? data.purchaseInfo?.remainingDownloads ?? 0;
  const actualTotalDownloads = totalDownloads ?? data.purchaseInfo?.maxDownloads ?? 5;

  return (
    <Card
      ref={enableScrollAnimation ? ref : null}
      $isPurchased={!!(data.isPurchased && showPurchasedActions)}
      $isInView={enableScrollAnimation ? isInView : true}
      $animationDelay={animationDelay}
    >
      <CoverSection
        as="button"
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        aria-label={`${isCurrentlyPlaying ? '일시정지' : '재생'} - ${data.title} by ${data.artistName}`}
      >
        <OptimizedImage
          src={getCoverImageUrl(data.coverImage)}
          alt={`${data.title} - ${data.artistName} 앨범 커버`}
          width={336}
          height={336}
          aspectRatio="square"
          quality={80}
          priority={enableScrollAnimation ? 'auto' : 'high'}
          enableWebP={true}
          fallback={Icons.defaultArtistBgImage}
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, (max-width: 1440px) 33vw, 336px"
        />
        <PlayOverlay className="play-overlay" $isPlaying={isCurrentlyPlaying} aria-hidden="true">
          <PlayIconWrapper>
            {isCurrentlyPlaying ? <Icon name="playSonic" size={180} /> : <Icon name="play" size={40} />}
          </PlayIconWrapper>
        </PlayOverlay>
        <DurationBadge>
          <Icon name="clock" />
          <span>{data.duration}</span>
        </DurationBadge>
      </CoverSection>

      <InfoSection
        onClick={handleInfoClick}
        onKeyDown={handleInfoKeyDown}
        role="button"
        tabIndex={0}
        aria-label={`${data.title} 상세 정보 보기`}
      >
        <CardTitle>{sanitizeText(data.title)}</CardTitle>
        <SubTitle style={{ color: theme.colors.fontGray[100] }}>{sanitizeText(data.artistName)}</SubTitle>

        {/* 구매하지 않은 mypage 타입일 때만 판매 종료일 표시 */}
        {!data.isPurchased && type === 'mypage' && data.saleInfo.endDate && (
          <EndDate>End of sale : {formatDate(data.saleInfo.endDate)}</EndDate>
        )}

        <SupplyContainer>
          <SupplyText>
            Supply {data.nft.soldCount}/{data.nft.totalSupply}
          </SupplyText>
          <ProgressBar percentage={purchaseRate} width="284px" height={6} />
        </SupplyContainer>

        <PriceContainer>
          <PriceWrapper>
            <PriceKRW>{formatCommas(Number(data.priceKrw))} KRW</PriceKRW>
          </PriceWrapper>

          {/* 마이페이지에서 불러올 때 && isPurchased 가 true 일 때만 표시하지 않기 */}
          {!(type === 'mypage' && data.isPurchased) && (
            <BuyButtonComponent
              onClick={e => handleInfoClick(e)}
              isPurchased={data.isPurchased}
              soldOut={data.saleInfo.status === 'SOLDOUT' || data.nft.soldCount >= data.nft.totalSupply}
              salesEnded={data.saleInfo.status === 'ENDED'}
              isInCart={data.isInCart}
              isScheduled={data.saleInfo.status === 'SCHEDULED'}
            />
          )}
        </PriceContainer>

        {/* 구매 완료시 액션 버튼들 */}
        {type !== 'main' && data.isPurchased && showPurchasedActions && (
          <PurchasedButtonGroup>
            <GradientButton
              onClick={e => {
                e.stopPropagation();
                onCertificateClick?.();
              }}
              variant="primary"
              size="medium"
              width="100%"
              icon={<Icon name="certificateIcon" />}
              style={{
                fontSize: '22px',
                backgroundColor: theme.colors.bgWhite,
                color: theme.colors.fontBlackText,
                border: `1px solid ${theme.colors.borderGraySecondary}`,
              }}
              aria-label="소유권 인증서 보기"
            >
              Certificate
            </GradientButton>

            <GradientButton
              onClick={e => {
                e.stopPropagation();
                onDownloadClick?.();
              }}
              variant="primary"
              size="medium"
              width="100%"
              icon={<Icon name="download" />}
              disabled={actualRemainingDownloads === 0}
              aria-label={`다운로드 (남은 횟수: ${actualRemainingDownloads}/${actualTotalDownloads})`}
            >
              Download
              <DownloadCount aria-hidden="true">
                {actualRemainingDownloads}/{actualTotalDownloads}
              </DownloadCount>
            </GradientButton>

            {data.isInMyWallet ? (
              <GradientButton
                disabled
                variant="primary"
                size="medium"
                width="100%"
                icon={<img src={Icons.inMyWallet} alt="" role="presentation" />}
                iconSize={24}
                style={{
                  fontSize: '22px',
                  fontWeight: '700',
                  background: theme.colors.bgWhite,
                  color: theme.colors.fontBlack,
                  opacity: 1,
                }}
                aria-label="내 지갑에 있음"
              >
                In My Wallet
              </GradientButton>
            ) : (
              <GradientButton
                onClick={e => {
                  e.stopPropagation();
                  onTransferClick?.();
                }}
                variant="gradient"
                size="medium"
                width="100%"
                icon={<Icon name="wallet" />}
                aria-label="내 지갑으로 전송"
              >
                Send to my wallet
              </GradientButton>
            )}
          </PurchasedButtonGroup>
        )}
      </InfoSection>
    </Card>
  );
};

const Card = styled.div<{
  $isPurchased: boolean;
  $isInView?: boolean;
  $animationDelay?: number;
}>`
  width: 100%;
  max-width: 336px;
  height: auto;
  flex-shrink: 0;
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.colors.borderGray};
  background: ${({ theme }) => theme.colors.bgWhite};
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;

  /* 스크롤 애니메이션 */
  opacity: ${({ $isInView }) => ($isInView ? 1 : 0)};
  transform: ${({ $isInView }) => ($isInView ? 'translateY(0)' : 'translateY(30px)')};
  transition:
    opacity 0.6s ease-out ${({ $animationDelay }) => $animationDelay}s,
    transform 0.6s ease-out ${({ $animationDelay }) => $animationDelay}s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  }
`;

// CoverImage removed - now using OptimizedImage component

const DurationBadge = styled.div`
  position: absolute;
  right: 16px;
  bottom: 8px;
  display: inline-flex;
  padding: 0 8px;
  align-items: center;
  gap: 4px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.fontBlack};

  svg {
    width: 16px;
    height: 16px;
  }

  span {
    color: ${({ theme }) => theme.colors.fontWhite};
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 31px;
  }
`;

const InfoSection = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`;

const EndDate = styled.p`
  color: ${({ theme }) => theme.colors.fontBlackText};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin: 0;
`;

const SupplyContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const SupplyText = styled.span`
  color: ${({ theme }) => theme.colors.fontBlackText};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const PriceContainer = styled(FlexSBCBox)`
  margin-top: 16px;
`;

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const PriceKRW = styled.span`
  color: ${({ theme }) => theme.colors.fontBlackText};
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const PurchasedButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
  width: 100%;
`;

const CoverSection = styled.div`
  position: relative;
  width: 100%;
  height: 352px;
  flex-shrink: 0;
  overflow: hidden;
  cursor: pointer;
  border-radius: 20px 20px 0 0;
  border: none;
  background: none;
  padding: 0;
  text-align: left;

  ${hoverPlayOverlay}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 250px;
  }
`;
const DownloadCount = styled.span`
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.fontWhite};
`;
